<template>
  <div class="wt-asset-audio-controls-area wt2-sg">
    <div class="-section-banner">
      <div class="wt-audio-area-title wt2-text-xl -line-height">
        {{ 'AudioLeftBar.audio' | culture }}
      </div>
      <div>
        <span class="-explanation">{{
          'AudioLeftBar.choosePreset' | culture
        }}</span>
        <span v-if="showTabs" class="wt2-text-n2">
          Note! You can adjust different volumes for parts of the clip that do
          and don't overlap with voice-over.
        </span>
      </div>
    </div>

    <wt-tabs
      v-if="showTabs"
      type="horizontal bottom"
      :tabs="$data.wtTabs"
      :current-tab-id="currentTabId"
      @tab-selected="$_onTabSelected"
    />

    <div v-if="$data.tabs.novo.selected" class="-tab-content">
      <div class="wt-volume-controls">
        <wt-slider
          v-if="isFragment && hasValidAudio && isPaceNormal"
          v-model="volume.video"
          @change="sliderChanged('changedVideoVolume')"
        >
          <i slot="icon" class="wticon wticon-camera" />
          <span slot="header">{{ 'AudioLeftBar.video' | culture }}</span>
        </wt-slider>

        <wt-slider
          v-else-if="isFragment && hasValidAudio && !isPaceNormal"
          :value="0"
          :disabled="true"
        >
          <i slot="icon" class="wticon wticon-camera" />
          <span slot="header">
            {{ 'AudioLeftBar.video' | culture }}
            <span
              class="wticon wticon-information1 tooltip-info"
              v-tooltip="noVideoAudioTooltip"
            />
          </span>
        </wt-slider>

        <wt-slider
          v-if="hasBG"
          v-model="volume.bgMusic"
          @change="sliderChanged('changedBgMusicVolume')"
        >
          <i slot="icon" class="wticon wticon-music-alt" />
          <span slot="header">{{ 'AudioLeftBar.BGMusic' | culture }}</span>
        </wt-slider>
        <wt-slider v-else :value="0" :disabled="true">
          <i slot="icon" class="wticon wticon-music-alt" />
          <span slot="header">
            {{ 'AudioLeftBar.BGMusic' | culture }}
            <span class="-gray">{{ 'AudioLeftBar.NoBGMusic' | culture }}</span>
          </span>
        </wt-slider>

        <wt-slider
          v-if="hasOverlayTracks"
          v-model="volume.overlayTracks"
          :disabled="true"
        >
          <i slot="icon" class="wticon wticon-camera" />
          <span slot="header">
            {{ 'AudioLeftBar.collageAsset' | culture }}
            <span
              class="wticon wticon-information1 tooltip-info"
              v-tooltip="overlayTracksTooltip"
            />
          </span>
        </wt-slider>
      </div>
    </div>

    <div v-if="$data.tabs.vo.selected" class="-tab-content">
      <div class="wt-volume-controls">
        <wt-slider
          v-if="isFragment && hasValidAudio && isPaceNormal"
          v-model="voVolume.video"
          @change="sliderChanged('changedVideoVolumeVoPlaying')"
        >
          <i slot="icon" class="wticon wticon-camera" />
          <span slot="header">{{ 'AudioLeftBar.video' | culture }}</span>
        </wt-slider>
        <wt-slider
          v-else-if="isFragment && hasValidAudio && !isPaceNormal"
          :value="0"
          :disabled="true"
        >
          <i slot="icon" class="wticon wticon-camera" />
          <span slot="header">
            {{ 'AudioLeftBar.video' | culture }}
            <span
              class="wticon wticon-information1 tooltip-info"
              v-tooltip="noVideoAudioTooltip"
            />
          </span>
        </wt-slider>

        <wt-slider
          v-if="hasBG"
          v-model="voVolume.bgMusic"
          @change="sliderChanged('changedBgMusicVolumeVoPlaying')"
        >
          <i slot="icon" class="wticon wticon-music-alt" />
          <span slot="header">{{ 'AudioLeftBar.BGMusic' | culture }}</span>
        </wt-slider>
        <wt-slider v-else :value="0" :disabled="true">
          <i slot="icon" class="wticon wticon-music-alt" />
          <span slot="header">
            {{ 'AudioLeftBar.BGMusic' | culture }}
            <span class="-gray">{{ 'AudioLeftBar.NoBGMusic' | culture }}</span>
          </span>
        </wt-slider>

        <wt-slider v-model="voVolume.narration" :show-slider="true">
          <i slot="icon" class="wticon wticon-mic" />
          <span slot="header">{{ 'AudioLeftBar.voiceOver' | culture }}</span>
        </wt-slider>
      </div>
    </div>

    <div class="wt-volume-apply-to-all">
      <div class="-label wt2-text-n2">
        {{ 'AudioLeftBar.applyToAll' | culture }}
      </div>
      <div class="-background wt2-sg">
        <wt-check-box
          class="-checkbox"
          v-model="applyVolumeToAll"
          :label-right="applyAllLabel"
        />
        <div
          v-if="!isFragment"
          v-tooltip="applyAllTooltip"
          class="wticon wticon-information1"
          data-original-title
          title
        />
      </div>
    </div>
  </div>
</template>
<script>
/** (GW)
 * Asset Editor's audio control component (GW)
 */
import app from 'app';
import Analytics from 'c/WtAnalytics';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import PageMixin from 'AssetEditor/pages/PageMixin.vue';
import {
  modelToUiControls,
  uiControlsToModel,
} from 'AssetEditor/functionality/Audio/convertAudio';
import WtSliderComponent from 'components/sliders/wtSlider.component.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

export default {
  mixins: [PageMixin],
  components: {
    'wt-slider': WtSliderComponent,
  },

  data() {
    const tabs = {
      vo: {
        id: 'vo',
        selected: false,
        label: 'With voice over',
        classes: { backgroundColor: 'wt2-bgcolor-primary' },
      },
      novo: {
        id: 'novo',
        selected: true,
        label: 'Without voice over',
        classes: { backgroundColor: 'wt2-bgcolor-primary' },
      },
    };

    return {
      maxVolume: 0.95,
      maxVolumeSteps: 100,
      applyVolumeToAll: this.$store.state.assetEditor.applyVolumeToAll,
      originalApplyVolumeToAll: this.$store.state.assetEditor.applyVolumeToAll,
      volume: {},
      voVolume: {},
      analytics: {
        preset: 'None',
        changedVideoVolume: false,
        changedBgMusicVolume: false,
        changedVideoVolumeVoPlaying: false,
        changedBgMusicVolumeVoPlaying: false,
      },
      noVideoAudioTooltip: {
        title: app.culture.get('AudioLeftBar.noVideoAudioTooltip'),
        placement: 'right',
        delay: 250,
        icon: 'wticon-information1',
      },
      overlayTracksTooltip: {
        title: app.culture.get('AudioLeftBar.overlayTracksTooltip'),
        placement: 'right',
        delay: 250,
        icon: 'wticon-information1',
      },
      tabs,
      wtTabs: [tabs.vo, tabs.novo],
    };
  },

  beforeDestroy() {
    bus.$off('apply', this.apply);
  },

  created() {
    const volume = modelToUiControls(this.volumeControl, this.hasValidAudio);
    const voVolume = modelToUiControls(
      this.onVoPlayVolumeControl,
      this.hasValidAudio
    );

    this.$$set(this.volume, volume);
    this.$$set(this.voVolume, voVolume);

    this.$watch((vm) => [vm.volume, vm.voVolume], this.$_volumeChangedHandler, {
      deep: true,
    });

    if (this.isInsideOfNarration || this.narrationAssets.length) {
      this.$_selectTab('vo');
    } else {
      this.$_selectTab('novo');
    }
  },

  mounted() {
    bus.$on('apply', this.apply);
  },

  computed: {
    ...mapGetters('assetEditor', ['workModel', 'AGD']),
    ...mapState('assetEditor', {
      userInteractedWithPage: (state) =>
        state.pageTmpState.userInteractedWithPage,
      narrationAssets: (state) => state.narrationAssets,
    }),

    volumeControl() {
      return this.workModel.volumeControl || {};
    },

    onVoPlayVolumeControl() {
      return this.workModel.onVoPlayVolumeControl || {};
    },

    applyAllLabel() {
      return this.isFragment
        ? app.culture.get('AudioLeftBar.applyToAllVideos')
        : app.culture.get('AudioLeftBar.applyToAllNoneVideos');
    },

    applyAllTooltip() {
      return {
        title: app.culture.get('AudioLeftBar.appliesToTimeline'),
        placement: 'bottom',
        delay: 250,
      };
    },

    isInsideOfNarration() {
      let rv = false;

      if (this.narrationAssets.length === 1) {
        const vo = this.narrationAssets[0];
        rv =
          vo.displayStartTime <= this.workModel.displayStartTime &&
          vo.displayEndTime >= this.workModel.displayEndTime;
      }

      return rv;
    },

    showTabs() {
      return this.narrationAssets.length && !this.isInsideOfNarration;
    },

    currentTabId() {
      return _.find(this.$data.tabs, { selected: true }).id;
    },
  },

  watch: {
    applyVolumeToAll() {
      this.setApplyVolumeToAll(this.applyVolumeToAll);
      this.allowToNavigateAway(!this.applyVolumeToAll);
    },
  },

  methods: {
    ...mapMutations('assetEditor', [
      'setVolumeControl',
      'setApplyVolumeToAll',
      'setTmpPageState',
      'setAGD',
    ]),
    ...mapActions('assetEditor', ['allowToNavigateAway', 'changeCurrentPage']),

    $_selectTab(tabId) {
      _.each(this.$data.tabs, (tab, id) => {
        tab.selected = id === tabId;
      });
    },

    $_onTabSelected(tab) {
      this.$_selectTab(tab.id);
    },

    backToPreview() {
      this.askSaveChanges(this.userInteractedWithPage);
    },

    apply() {
      const newVolumeControl = uiControlsToModel(this.volume);
      const newOnVoPlayVolumeControl = uiControlsToModel(this.voVolume);

      const hasSound = this.hasValidAudio ? 'Yes' : 'No';
      Analytics.trigger('analytics:audio:audioSettings', {
        'Asset Has Sound': hasSound,
        'Selected preset': this.analytics.preset,
        'Changed video volume': this.analytics.changedVideoVolume,
        'Changed music volume': this.analytics.changedBgMusicVolume,
        'Changed video volume when vo playing':
          this.analytics.changedVideoVolumeVoPlaying,
        'Changed music volume when vo playing':
          this.analytics.changedBgMusicVolumeVoPlaying,
      });

      this.setVolumeControl({
        volumeControl: newVolumeControl,
        onVoPlayVolumeControl: newOnVoPlayVolumeControl,
      });

      if (!newVolumeControl.video) {
        this.setAGD({ showTranscription: false });
      }

      this.allowToNavigateAway(true);
      this.changeCurrentPage('preview');
    },

    sliderChanged(name) {
      this.analytics[name] = true;
    },

    $_volumeChangedHandler() {
      bus.$emit('volumeChange', {
        volumeControl: uiControlsToModel(this.volume),
        onVoPlayVolumeControl: uiControlsToModel(this.voVolume),
      });
      this.setTmpPageState({
        userInteractedWithPage: true,
      });
    },
  },
};
</script>

<style lang="scss">
.wt-asset-audio-controls-area {
  > div {
    padding-left: 87px;
    padding-right: 87px;
  }

  .wt2-slider {
    padding: 0;
    .-label-box {
      .-gray {
        color: $color-grey-0;
      }
      > .wticon {
        width: 28px;
        display: flex;
        align-items: center;
      }
      .wticon-camera {
        font-size: 13px;
      }
      .wticon-music-alt {
        font-size: 18px;
      }
      .wticon-mic {
        font-size: 20px;
      }
    }
  }

  span.tooltip-info {
    margin-left: 10px;
  }

  .wt2-check-box-component > .-body {
    background-color: white;
  }

  > .-section-banner {
    .wt-audio-area-title {
      padding-bottom: 5px;
    }
  }

  .-explanation {
    font-size: 14px;
    color: $color-grey-0;
  }

  > .wt2-tabs {
    padding: 0;
    margin: 30px 87px 10px 87px;
    height: 34px;
    border-bottom: 1px solid #ccc;
    align-self: center;

    .wt2-tabs-menu .wt2-tabs-menu-tab {
      width: 50%;
      padding: 0 15px;
      justify-content: center;
      font-size: $font-size-n;
    }
  }

  .wt-audio-presets-icons {
    display: flex;
    justify-content: space-around;
    padding: 0;

    .wt-audio-preset {
      text-align: center;
      color: #4a4a4a;
      font-size: 12px;
      width: auto;
      &:not(.wt-disabled) {
        cursor: pointer;
        &:hover,
        &.active {
          i {
            background-color: $wt-blue;
            border-color: $wt-blue;
            color: $white;
          }
        }
      }

      &.wt-disabled {
        pointer-events: none;
        color: lighten(#4a4a4a, 40%);
        i {
          border-color: lighten(#4a4a4a, 40%);
        }
      }

      i {
        margin: 0 auto 5px auto;
        display: block;
        font-size: 20px;
        border-radius: 100%;
        border: 1px solid #4a4a4a;
        width: 40px;
        height: 40px;
        line-height: 40px;
        transition: all 0.1s ease-in;
        position: relative;

        &.wticon-music-alt-crossedout::before {
          font-size: 26px;
        }
        &.wticon-video_sound-crossedout::before {
          font-size: 26px;
        }
      }
    }
  }

  .wt-volume-controls {
    .-seek-label-box {
      margin-top: 30px;
    }

    .wt-volume-controls-row {
      display: flex;
      justify-content: space-between;
      font-size: 14px;
      margin-bottom: 25px;

      .wticon {
        margin-left: 5px;
        &.-error {
          color: $color-error;
        }
      }

      .wt-volume-control-label {
        flex-shrink: 0;
        flex-grow: 0;
      }

      .wt-volume-control-area {
        display: flex;
        flex-direction: row;

        .wt-volume-control-helper-text {
          font-size: 12px;
          pointer-events: none;
          flex-flow: column nowrap;
          justify-content: center;
          display: flex;
        }

        .wt-volume-control {
          display: flex;
          justify-content: center;
          flex-direction: row;
          position: relative;
          margin: 0 5px;

          .wt-volume-value-indicator {
            position: absolute;
            left: 6px;
            right: 6px;
            .-inner {
              position: absolute;
              top: -12px;
              left: 100%;
              font-size: 11px;
              letter-spacing: -0.35px;
              color: #4a4a4a;
              transform: translateX(-50%);
            }
          }

          input[type='range'] {
            width: 112px;
            cursor: pointer;
            -webkit-appearance: none;
            height: 2px;
            background-color: $volume-control-slider-color;
            background-position: center;
            background-repeat: no-repeat;
            margin-top: 9px;

            &:disabled {
              cursor: default;
            }

            &:active {
              &::-webkit-slider-thumb {
                background: darken($wt-blue, 10%);
              }
              &::-moz-range-thumb {
                background: darken($wt-blue, 10%);
              }
            }

            &::-webkit-slider-thumb {
              -webkit-appearance: none;
              width: 11px;
              height: 11px;
              border-radius: 50%;
              background-color: $wt-blue;
              position: relative;
              border: 0;
              cursor: pointer;
              transition: all 0.2s;

              &:hover,
              &:active {
                background: darken($wt-blue, 10%);
              }
            }
            &::-moz-range-thumb {
              background-color: $wt-blue;
            }
          }
        }
      }
    }

    .wt-volume-controls-row.wt-disabled {
      .wt-volume-control-area {
        color: $color-disabled;
        .wt-volume-control {
          &:before,
          &:after {
            color: lighten(#4a4a4a, 45%);
          }
          input[type='range'] {
            background-color: lighten(#4a4a4a, 45%);
            &::-webkit-slider-thumb {
              background: $volume-control-slider-color;
              cursor: default;
              &:hover {
                background: $volume-control-slider-color;
                cursor: default;
              }
            }
          }
        }
      }
    }
  }

  .wt-volume-controls-row-error-bg-music {
    margin-top: -10px;
    margin-bottom: 5px;

    span {
      color: #d0021b;
      font-size: 12px;
    }
  }

  .wt-volume-apply-to-all {
    margin-top: 30px;
    margin-bottom: 30px;
    border-top: $color-black-l 1px solid;
    padding-top: 30px;
    display: flex;
    justify-content: space-between;
    flex-flow: column;

    > .-label {
      margin-bottom: 10px;
    }
    > .-background {
      display: flex;
      align-items: center;
      justify-content: space-between;
      .-checkbox {
        flex: 1;
      }
    }
  }

  .wticon-information1,
  .-error {
    cursor: pointer;
    font-size: 16px;
  }
}
</style>
